import React, { useState, useEffect, useMemo } from "react"
import { Row, Col, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap"
import { AvForm, AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"
import api from "API/Api"
import classnames from "classnames"
import jsPDF from "jspdf"
import "jspdf-autotable"
import Select from "react-select"
import loader from "./../../assets/loading/loader.gif"

const producerDisplayParams = [
  { value: "companyName", label: "Company" },
  { value: "plantName", label: "Plant" },
  { value: "ccNumber", label: "CC Number" },
  { value: "ccName", label: "CC Name" },
  { value: "routeNumber", label: "Route Number" },
  { value: "routeName", label: "Route Name" },
  { value: "centerNumber", label: "Center Number" },
  { value: "centerName", label: "Center Name" },
  { value: "ccOperationType", label: "Center Operation Type" },
  { value: "settingBillingType", label: "Center Billing Type" },
  { value: "producerNumber", label: "Producer Number" },
  { value: "producerName", label: "Producer Name" },
  { value: "producerRfid", label: "RFID" },
  { value: "shortNo", label: "Short Number" },
  { value: "milkType", label: "Default Milk Type" },
  { value: "producerGender", label: "Gender" },
  { value: "producerMobile", label: "Mobile" },
  { value: "door", label: "Door No" },
  { value: "village", label: "Village Name" },
  { value: "taluk", label: "Taluk Name" },
  { value: "district", label: "District Name" },
  { value: "pincode", label: "Pincode" },
  { value: "producerAadhar", label: "Aadhaar No" },
  { value: "producerPAN", label: "PAN Number" },
  { value: "producerEmail", label: "Email" },
  { value: "totalCows", label: "No of Cows" },
  { value: "totalBuffalos", label: "No of Buffalos" },
  { value: "agricultureLand", label: "Agricultural Land" },
  { value: "community", label: "Community" },
  { value: "paymentMode", label: "Pay Mode" },
  { value: "status", label: "Status" },
  { value: "created_at", label: "Added Datetime" },
  { value: "updated_at", label: "Updated Datetime" },
  { value: "firstPoured", label: "First Poured" },
  { value: "lastPoured", label: "Last Poured" },
  { value: "remarks", label: "Remarks" },
]

const vendorDisplayParams = [
  { value: "companyName", label: "Company" },
  { value: "plantName", label: "Plant" },
  { value: "ccNumber", label: "CC Number" },
  { value: "ccName", label: "CC Name" },
  { value: "routeNumber", label: "Route Number" },
  { value: "routeName", label: "Route Name" },
  { value: "vendorNumber", label: "Vendor Number" },
  { value: "vendorName", label: "Vendor Name" },
  { value: "vendorTypeName", label: "Vendor Type" },
  { value: "operationType", label: "Operation Type" },
  { value: "virtualCenter", label: "Is Virtual Center" },
  { value: "canNumber", label: "Can Number" },
  { value: "contactName", label: "Contact Name" },
  { value: "contactEmail", label: "Contact Email" },
  { value: "contactAadhar", label: "Contact Aadhaar" },
  { value: "contactPAN", label: "Contact PAN" },
  { value: "supervisor", label: "Supervisor" },
  { value: "paymentMode", label: "Payment Mode" },
  { value: "bankAccName", label: "Bank Acc Name" },
  { value: "bankAccNo", label: "Bank Acc No" },
  { value: "bankAccType", label: "Bank Acc Type" },
  { value: "pickupVehicle", label: "Pick up Vehicle" },
  { value: "applicableMilkTypes", label: "Applicable Milk Type" },
  { value: "defaultMilkType", label: "Default Milk Type" },
  { value: "created_at", label: "Added Date Time" },
  { value: "updated_at", label: "Updated Date Time" },
  { value: "firstPoured", label: "First Poured" },
  { value: "lastPoured", label: "Last Poured" },
  { value: "remarks", label: "Remarks" },
]

const vendorDefaultSelected = vendorDisplayParams.filter(p =>
  ["ccNumber", "routeNumber", "vendorNumber", "vendorName", "created_at", "updated_at"].includes(p.value)
)

const opTypeMap = { "11": "Direct Company Center", "12": "Agent Center", "13": "Independent Center" }
const billingTypeMap = { "0": "Producer Billing", "1": "Agent Billing" }
const payModeMap = { "1": "Cash", "2": "Bank Account", "4": "Loan Account" }

const ProducerReport = () => {
  const [activeTab, setActiveTab] = useState("1")
  // Shared master data
  const [allPlants, setAllPlants] = useState([])
  const [plants, setPlants] = useState([])
  const [allCenters, setAllCenters] = useState([])
  const [centers, setCenters] = useState([])
  const [routes, setRoutes] = useState([])
  const [collections, setCollections] = useState([])
  const [companies, setCompanies] = useState([])

  // Tab 1 state
  const [p_loading, p_setLoading] = useState(false)
  const [p_submitted, p_setSubmitted] = useState(false)
  const [p_results, p_setResults] = useState([])
  const [p_selectedParams, p_setSelectedParams] = useState([])
  const [p_orderParams, p_setOrderParams] = useState(false)
  const [p_search, p_setSearch] = useState("")

  // Tab 2 state
  const [v_loading, v_setLoading] = useState(false)
  const [v_submitted, v_setSubmitted] = useState(false)
  const [v_results, v_setResults] = useState([])
  const [v_selectedParams, v_setSelectedParams] = useState(vendorDefaultSelected)
  const [v_orderParams, v_setOrderParams] = useState(false)
  const [v_search, v_setSearch] = useState("")

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    try {
      const [cResp, pResp, coResp] = await Promise.all([
        api.get("centers"), api.get("plants"), api.get("companies")
      ])
      const ccData = Array.isArray(cResp.data.data) ? cResp.data.data : cResp.data.data?.data || []
      setAllCenters(ccData); setCenters(ccData)
      const pData = Array.isArray(pResp.data.data) ? pResp.data.data : pResp.data.data?.data || []
      setAllPlants(pData); setPlants(pData)
      setCompanies(Array.isArray(coResp.data.data) ? coResp.data.data : coResp.data.data?.data || [])
    } catch (e) { console.error(e) }
  }

  const showCompany = companies.length > 1

  const companyChange = async e => {
    const val = e.target.value
    setCenters(allCenters); setRoutes([]); setCollections([])
    if (val && val !== "") {
      try { const r = await api.get("plants-by-company/" + val); setPlants(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch(e){}
    } else setPlants(allPlants)
  }
  const plantChange = async e => {
    const val = e.target.value; setRoutes([]); setCollections([])
    if (val && val !== "") {
      try { const r = await api.get("chillings-by-plant/" + val); setCenters(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch(e){}
    } else setCenters(allCenters)
  }
  const ccChange = async e => {
    try { const u = !e.target.value || e.target.value == "0" ? "routes" : "route-cc/" + e.target.value
      const r = await api.get(u); setRoutes(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      setCollections([])
    } catch(e){}
  }
  const routeChange = async e => {
    try { const u = !e.target.value || e.target.value == "0" ? "collection-centers" : "collection-center-route/" + e.target.value
      const r = await api.get(u); setCollections(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
    } catch(e){}
  }

  // Tab 1 handlers
  const handleProducerSubmit = async (e, v) => {
    p_setLoading(true)
    try {
      if (companies.length === 1) v.company = companies[0].id
      const resp = await api.post("reports/producer-member", v)
      if (resp.data.success) { p_setResults(resp.data.data || []); p_setSubmitted(true) }
    } catch (err) { console.error(err) }
    p_setLoading(false)
  }

  // Tab 2 handlers
  const handleVendorSubmit = async (e, v) => {
    v_setLoading(true)
    try {
      if (companies.length === 1) v.company = companies[0].id
      const resp = await api.post("reports/vendor-member", v)
      if (resp.data.success) { v_setResults(resp.data.data || []); v_setSubmitted(true) }
    } catch (err) { console.error(err) }
    v_setLoading(false)
  }

  const p_pVals = p_selectedParams.map(p => p.value)
  const v_pVals = v_selectedParams.map(p => p.value)

  const p_filtered = useMemo(() => {
    if (!p_search) return p_results
    const s = p_search.toLowerCase()
    return p_results.filter(r => Object.values(r).some(v => v && String(v).toLowerCase().includes(s)))
  }, [p_results, p_search])

  const v_filtered = useMemo(() => {
    if (!v_search) return v_results
    const s = v_search.toLowerCase()
    return v_results.filter(r => Object.values(r).some(v => v && String(v).toLowerCase().includes(s)))
  }, [v_results, v_search])

  const exportCSV = (data, cols, filename) => {
    if (!data.length || !cols.length) return
    const headers = cols.map(c => c.label)
    const rows = data.map(r => cols.map(c => {
      let v = r[c.value] ?? ""
      if (c.value === "ccOperationType") v = opTypeMap[v] || v
      if (c.value === "settingBillingType") v = billingTypeMap[v] || v
      return v
    }))
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v || "").replace(/"/g, '""')}"`).join(",")).join("\n")
    const blob = new Blob([csv], { type: "text/csv" })
    const u = URL.createObjectURL(blob)
    const a = document.createElement("a"); a.href = u; a.download = filename; a.click(); URL.revokeObjectURL(u)
  }

  const downloadPdf = (tableId, filename) => {
    const pdf = new jsPDF("l")
    pdf.autoTable({ html: "#" + tableId, styles: { fontSize: 7 } })
    pdf.save(filename)
  }

  const renderOrderParams = (selectedParams) => {
    if (!selectedParams.length) return <div style={{ color: "#94a3b8", fontSize: "12px", padding: "8px" }}>Select display parameters first</div>
    return (
      <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: "4px" }}>
        {selectedParams.map((p, i) => (
          <div key={p.value} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", borderBottom: "1px solid #f1f5f9", fontSize: "12px", background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
            <span>{p.label}</span>
            <i className="fas fa-arrows-alt-v" style={{ color: "#94a3b8" }}></i>
          </div>
        ))}
      </div>
    )
  }

  const formatCellValue = (row, col) => {
    const v = row[col.value]
    if (col.value === "ccOperationType") return opTypeMap[v] || v || "-"
    if (col.value === "settingBillingType") return billingTypeMap[v] || v || "-"
    if (col.value === "status") return v === "active" || v === "1" ? "Active" : "Inactive"
    if (col.value === "paymentMode") return payModeMap[v] || v || "-"
    return v || "-"
  }

  const renderTable = (data, selectedParams, tableId, searchVal, setSearch) => {
    const cols = selectedParams
    if (!cols.length) return <div className="text-center py-4 text-muted">Please select display parameters and submit</div>
    return (
      <>
        <div className="notToPrint" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", flexWrap: "wrap", gap: "8px" }}>
          <div style={{ fontSize: "13px", color: "#64748b" }}>
            Showing <strong>{data.length}</strong> records
          </div>
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <input type="text" className="form-control form-control-sm" placeholder="Search..." style={{ width: "180px" }} value={searchVal} onChange={e => setSearch(e.target.value)} />
            <button className="btn btn-sm btn-outline-success" onClick={() => exportCSV(data, cols, tableId + ".csv")} title="Export CSV"><i className="fas fa-file-csv"></i></button>
            <button className="btn btn-sm btn-outline-danger" onClick={() => downloadPdf(tableId, tableId + ".pdf")} title="Export PDF"><i className="fas fa-file-pdf"></i></button>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => window.print()} title="Print"><i className="fas fa-print"></i></button>
          </div>
        </div>
        <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
          <table className="table table-sm table-hover table-bordered mb-0" id={tableId} style={{ fontSize: "11px" }}>
            <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 1 }}>
              <tr>
                <th style={{ fontSize: "10px", color: "#64748b" }}>S.No</th>
                {cols.map(c => <th key={c.value} style={{ fontSize: "10px", color: "#64748b", whiteSpace: "nowrap" }}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.map((r, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  {cols.map(c => <td key={c.value} style={{ whiteSpace: "nowrap" }}>{formatCellValue(r, c)}</td>)}
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={cols.length + 1} className="text-center py-4 text-muted">No records found</td></tr>}
            </tbody>
          </table>
        </div>
      </>
    )
  }

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader notToPrint">
        <Row><Col md={12}><h4>Producer Report</h4></Col></Row>
      </div>
      <div className="dashContent">
        <Nav tabs className="notToPrint">
          <NavItem><NavLink className={classnames({ active: activeTab === "1" })} onClick={() => setActiveTab("1")} style={{ cursor: "pointer" }}>VLCC Member Report</NavLink></NavItem>
          <NavItem><NavLink className={classnames({ active: activeTab === "2" })} onClick={() => setActiveTab("2")} style={{ cursor: "pointer" }}>Center / Vendor Report</NavLink></NavItem>
        </Nav>
        <TabContent activeTab={activeTab} style={{ paddingTop: "16px" }}>
          {/* ═══ TAB 1: VLCC Member Report ═══ */}
          <TabPane tabId="1">
            <AvForm className="form-horizontal notToPrint" onValidSubmit={(e, v) => handleProducerSubmit(e, v)}>
              {showCompany && <Row>
                <Col md={3}><div className="mb-3"><AvField type="select" name="company" label="Company" onChange={companyChange}><option value="">All</option>{companies.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
              </Row>}
              <Row>
                <Col md={3}><div className="mb-3"><AvField type="select" name="plant" label="Plant" onChange={plantChange}><option value="">All</option>{plants.map((p, i) => <option key={i} value={p.id}>{p.name}</option>)}</AvField></div></Col>
                <Col md={3}><div className="mb-3"><AvField type="select" name="chillingCenter" label="Chilling Center" onChange={ccChange}><option value="">All</option><option value="0">All</option>{centers.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
                <Col md={3}><div className="mb-3"><AvField type="select" name="route" label="Route" onChange={routeChange}><option value="">All</option><option value="0">All</option>{routes.map((r, i) => <option key={i} value={r.id}>{r.routeName}</option>)}</AvField></div></Col>
                <Col md={3}><div className="mb-3"><AvField type="select" name="collectionCenter" label="Collection Center"><option value="">All</option><option value="0">All</option>{collections.map((c, i) => <option key={i} value={c.id}>{c.centerName}</option>)}</AvField></div></Col>
              </Row>
              <Row>
                <Col md={3}><div className="mb-3"><AvField type="select" name="operationType" label="Center Operation Type"><option value="">All</option><option value="11">Direct Company Center</option><option value="12">Agent Center</option><option value="13">Independent Center</option></AvField></div></Col>
                <Col md={3}><div className="mb-3"><AvField type="select" name="paymentMode" label="Payment Mode"><option value="">All</option><option value="1">Cash</option><option value="2">Bank Account</option><option value="4">Loan Account</option></AvField></div></Col>
                <Col md={3}><div className="mb-3"><AvField type="select" name="billingType" label="Center Billing Type"><option value="">All</option><option value="0">Producer Billing</option><option value="1">Agent Billing</option></AvField></div></Col>
                <Col md={3}><div className="mb-3"><AvField type="select" name="status" label="Status"><option value="">All</option><option value="1">Active</option><option value="0">Inactive</option></AvField></div></Col>
              </Row>
              <Row>
                <Col md={3}><div className="mb-3"><AvField type="select" name="addedUpdated" label="Added/Updated"><option value="">All</option><option value="1">Added Between Period</option><option value="2">Updated Between Period</option></AvField></div></Col>
                <Col md={3}><div className="mb-3"><AvField type="date" name="fromDate" label="From Date" /></div></Col>
                <Col md={3}><div className="mb-3"><AvField type="date" name="toDate" label="To Date" /></div></Col>
                <Col md={3}><div className="mb-3"><AvRadioGroup inline name="showPurchaseParams" label="Show Purchase Parameters" value="0"><AvRadio label="Yes" value="1" /><AvRadio label="No" value="0" /></AvRadioGroup></div></Col>
              </Row>
              <Row>
                <Col md={4}><div className="mb-3"><label>Display Parameters</label><Select isMulti options={producerDisplayParams} value={p_selectedParams} onChange={v => p_setSelectedParams(v || [])} placeholder="Select columns..." className="basic-multi-select" classNamePrefix="select" /></div></Col>
                <Col md={1}><div className="mb-3" style={{ paddingTop: "28px" }}><label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}><input type="checkbox" checked={p_orderParams} onChange={e => p_setOrderParams(e.target.checked)} /> <span style={{ fontSize: "12px" }}>Order Parameters</span></label></div></Col>
                {p_orderParams && <Col md={3}><div className="mb-3"><label>Order Parameters</label>{renderOrderParams(p_selectedParams)}</div></Col>}
                <Col md={2}><div className="mb-3" style={{ paddingTop: "28px" }}><button type="submit" className="btn btn-primary"><i className="fas fa-check me-1"></i>Submit</button></div></Col>
              </Row>
            </AvForm>

            {p_loading && <div className="text-center py-4"><img className="loaderImg" src={loader} alt="Loading" /></div>}
            {p_submitted && !p_loading && renderTable(p_filtered, p_selectedParams, "producerMemberTbl", p_search, p_setSearch)}
          </TabPane>

          {/* ═══ TAB 2: Center / Vendor Report ═══ */}
          <TabPane tabId="2">
            <AvForm className="form-horizontal notToPrint" onValidSubmit={(e, v) => handleVendorSubmit(e, v)}>
              {showCompany && <Row>
                <Col md={3}><div className="mb-3"><AvField type="select" name="company" label="Company" onChange={companyChange}><option value="">All</option>{companies.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
              </Row>}
              <Row>
                <Col md={3}><div className="mb-3"><AvField type="select" name="plant" label="Plant" onChange={plantChange}><option value="">All</option>{plants.map((p, i) => <option key={i} value={p.id}>{p.name}</option>)}</AvField></div></Col>
                <Col md={3}><div className="mb-3"><AvField type="select" name="chillingCenter" label="Chilling Center" onChange={ccChange}><option value="">All</option><option value="0">All</option>{centers.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
                <Col md={3}><div className="mb-3"><AvField type="select" name="route" label="Route"><option value="">All</option><option value="0">All</option>{routes.map((r, i) => <option key={i} value={r.id}>{r.routeName}</option>)}</AvField></div></Col>
                <Col md={3}><div className="mb-3"><AvField type="select" name="vendorType" label="Vendor Type"><option value="">All</option><option value="1">Collection Center (VLCC)</option><option value="2">Bulk Vendor</option></AvField></div></Col>
              </Row>
              <Row>
                <Col md={3}><div className="mb-3"><AvField type="select" name="status" label="Status"><option value="">All</option><option value="1">Active</option><option value="0">Inactive</option></AvField></div></Col>
                <Col md={3}><div className="mb-3"><AvField type="select" name="addedUpdated" label="Added/Updated"><option value="">All</option><option value="1">Added Between Period</option><option value="2">Updated Between Period</option></AvField></div></Col>
                <Col md={3}><div className="mb-3"><AvField type="select" name="paymentMode" label="Payment Mode"><option value="">All</option><option value="1">Cash</option><option value="2">Bank Account</option><option value="4">Loan Account</option></AvField></div></Col>
                <Col md={3}><div className="mb-3"><AvRadioGroup inline name="showPurchaseParams" label="Show Purchase Parameters" value="0"><AvRadio label="Yes" value="1" /><AvRadio label="No" value="0" /></AvRadioGroup></div></Col>
              </Row>
              <Row>
                <Col md={3}><div className="mb-3"><AvField type="date" name="fromDate" label="From Date" /></div></Col>
                <Col md={3}><div className="mb-3"><AvField type="date" name="toDate" label="To Date" /></div></Col>
              </Row>
              <Row>
                <Col md={4}><div className="mb-3"><label>Display Parameters</label><Select isMulti options={vendorDisplayParams} value={v_selectedParams} onChange={v => v_setSelectedParams(v || [])} placeholder="Select columns..." className="basic-multi-select" classNamePrefix="select" /></div></Col>
                <Col md={1}><div className="mb-3" style={{ paddingTop: "28px" }}><label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}><input type="checkbox" checked={v_orderParams} onChange={e => v_setOrderParams(e.target.checked)} /> <span style={{ fontSize: "12px" }}>Order Parameters</span></label></div></Col>
                {v_orderParams && <Col md={3}><div className="mb-3"><label>Order Parameters</label>{renderOrderParams(v_selectedParams)}</div></Col>}
                <Col md={2}><div className="mb-3" style={{ paddingTop: "28px" }}><button type="submit" className="btn btn-primary"><i className="fas fa-check me-1"></i>Submit</button></div></Col>
              </Row>
            </AvForm>

            {v_loading && <div className="text-center py-4"><img className="loaderImg" src={loader} alt="Loading" /></div>}
            {v_submitted && !v_loading && renderTable(v_filtered, v_selectedParams, "vendorMemberTbl", v_search, v_setSearch)}
          </TabPane>
        </TabContent>
      </div>
    </div>
  )
}

export default ProducerReport
